import { useState, useMemo, useEffect } from 'react';
import { Package, AlertTriangle, Check } from 'lucide-react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { usePets } from '../../../contexts/PetsContext';
import { useToast } from '../../../contexts/ToastContext';
import { formatGrams } from '../../../lib/format';

/**
 * Modal para rellenar el dispenser desde la bolsa principal.
 *
 * Quick buttons: 1/4, 1/2, 3/4, "Lleno" — cada uno calcula la fracción de
 * la CAPACIDAD del dispenser y lo capa al mínimo entre eso, el espacio
 * libre, y la bolsa disponible. "Lleno" rellena hasta el techo.
 *
 * Checkbox "estaba vacío" (wasEmpty):
 *  - Cambia la base del cálculo a 0 (en vez de dispenserStockGr).
 *  - Cierra el ciclo de aprendizaje activo en la misma transacción.
 *  - Si el sistema creía que había stock, se loguea como discrepancia.
 *
 * Validaciones:
 *  - amount entero positivo
 *  - amount + base ≤ capacidad (base es 0 si wasEmpty, sino dispenserStockGr)
 *  - amount ≤ mainStock
 */
const RefillModal = ({ group, dispenser, isOpen, onClose, onSuccess }) => {
  const { refillDispenser } = usePets();
  const toast = useToast();

  const [amount, setAmount] = useState('');
  const [wasEmpty, setWasEmpty] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Stage 2: el modal opera sobre un dispenser específico (el caller lo elige
  // cuando hay varios). Si no se pasa, asumimos el primero del grupo.
  const targetDispenser = dispenser ?? group.dispensers?.[0] ?? null;
  const dispenserCurrentStock = targetDispenser?.stockGr ?? 0;
  const dispenserCapacity = targetDispenser?.capacityGr ?? 0;

  const dispenserBase = wasEmpty ? 0 : dispenserCurrentStock;
  const availableSpace = Math.max(0, dispenserCapacity - dispenserBase);
  const maxRefill = Math.min(availableSpace, group.mainStockGr);

  useEffect(() => {
    if (isOpen) {
      setWasEmpty(false);
      setAmount('');
      setError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setAmount(maxRefill > 0 ? String(maxRefill) : '');
    }
  }, [isOpen, wasEmpty]); // eslint-disable-line react-hooks/exhaustive-deps

  const quickAmounts = useMemo(() => {
    const cap = dispenserCapacity;
    return [
      { label: '1/4', value: Math.min(Math.round(cap * 0.25), maxRefill) },
      { label: '1/2', value: Math.min(Math.round(cap * 0.5), maxRefill) },
      { label: '3/4', value: Math.min(Math.round(cap * 0.75), maxRefill) },
      { label: 'Lleno', value: maxRefill },
    ];
  }, [dispenserCapacity, maxRefill]);

  const amountNum = Number(amount);
  const isValid =
    Number.isInteger(amountNum) &&
    amountNum > 0 &&
    amountNum <= availableSpace &&
    amountNum <= group.mainStockGr;

  const validationMessage = (() => {
    if (!amount || amountNum === 0) return null;
    if (!Number.isInteger(amountNum) || amountNum < 0) return 'Cantidad inválida';
    if (amountNum > group.mainStockGr)
      return `La bolsa solo tiene ${formatGrams(group.mainStockGr)}`;
    if (amountNum > availableSpace)
      return `Excede la capacidad del dispenser (espacio libre: ${formatGrams(availableSpace)})`;
    return null;
  })();

  const cycle = group.activeCycle;
  const showClosingCyclePanel = wasEmpty && cycle;
  const showDiscrepancyWarning = wasEmpty && dispenserCurrentStock > 0;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!isValid || !targetDispenser) return;
    try {
      setSubmitting(true);
      setError(null);
      const result = await refillDispenser(group.id, targetDispenser.id, amountNum, { wasEmpty });
      if (result?.closedCycleWasOutlier) {
        toast.info(
          `Cargaste ${formatGrams(amountNum)}. El ciclo se cerró como atípico (no afecta estimaciones).`
        );
      } else if (result?.closedCycleId) {
        toast.success(
          `Cargaste ${formatGrams(amountNum)} en ${targetDispenser.name}. Ciclo cerrado.`
        );
      } else {
        toast.success(`Cargaste ${formatGrams(amountNum)} en ${targetDispenser.name}.`);
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.message || 'No se pudo rellenar el dispenser.');
    } finally {
      setSubmitting(false);
    }
  };

  if (group.mainStockGr === 0) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Sin stock en la bolsa">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-slate-600">
            La bolsa principal está vacía. Agregá una bolsa nueva antes de rellenar el dispenser.
          </p>
          <Button variant="primary" fullWidth onClick={onClose}>
            Entendido
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={() => !submitting && onClose()} title={`Rellenar ${targetDispenser?.name ?? 'dispenser'}`}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="rounded-lg bg-slate-50 px-3 py-2.5 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-slate-500">{targetDispenser?.name ?? 'Dispenser'}</span>
            <span className="font-medium text-slate-900">
              {formatGrams(dispenserCurrentStock)} / {formatGrams(dispenserCapacity)}
            </span>
          </div>
          <div className="mt-1.5 flex items-center justify-between">
            <span className="text-slate-500">Bolsa disponible</span>
            <span className="font-medium text-slate-900">{formatGrams(group.mainStockGr)}</span>
          </div>
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">
            Cantidad a cargar
          </label>
          <div className="grid grid-cols-4 gap-2 mb-2">
            {quickAmounts.map((q) => (
              <button
                key={q.label}
                type="button"
                onClick={() => setAmount(String(q.value))}
                disabled={submitting || q.value === 0}
                className={[
                  'rounded-xl border px-2 py-2 text-sm font-medium transition-colors',
                  amountNum === q.value && q.value > 0
                    ? 'border-brand-500 bg-brand-50 text-brand-800'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
                  q.value === 0 ? 'opacity-50 cursor-not-allowed' : '',
                ].join(' ')}
              >
                <div>{q.label}</div>
                <div className="text-[10px] font-normal text-slate-500 mt-0.5">
                  {formatGrams(q.value)}
                </div>
              </button>
            ))}
          </div>
          <Input
            type="number"
            inputMode="numeric"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={validationMessage}
            min={1}
            max={maxRefill}
            placeholder="Cantidad en gramos"
          />
        </div>

        <button
          type="button"
          onClick={() => setWasEmpty((v) => !v)}
          disabled={submitting}
          aria-pressed={wasEmpty}
          className={[
            'flex w-full items-center justify-between gap-3 rounded-xl border px-3 py-3 text-left transition-colors',
            wasEmpty
              ? 'border-brand-500 bg-brand-50'
              : 'border-slate-200 bg-white hover:bg-slate-50',
            submitting ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
          ].join(' ')}
        >
          <span className="flex-1">
            <span className={['block text-sm font-medium', wasEmpty ? 'text-brand-900' : 'text-slate-900'].join(' ')}>
              ¿El dispenser estaba vacío?
            </span>
            <span className={['mt-0.5 block text-xs', wasEmpty ? 'text-brand-800' : 'text-slate-500'].join(' ')}>
              Cierra el ciclo de aprendizaje y arranca uno nuevo.
            </span>
          </span>
          <span
            aria-hidden="true"
            className={[
              'flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors',
              wasEmpty
                ? 'bg-brand-600 text-white'
                : 'border-2 border-slate-300 bg-white',
            ].join(' ')}
          >
            {wasEmpty && <Check className="h-4 w-4" strokeWidth={3} />}
          </span>
        </button>

        {showClosingCyclePanel && (
          <div className="rounded-lg bg-slate-50 px-3 py-2.5 text-sm">
            <p className="font-medium text-slate-900 mb-2">Ciclo que se va a cerrar</p>
            <div className="flex items-center justify-between text-slate-600">
              <span>Comidas servidas</span>
              <span className="font-medium text-slate-900">{cycle.feedingsCount ?? 0}</span>
            </div>
            <div className="mt-1 flex items-center justify-between text-slate-600">
              <span>Total cargado</span>
              <span className="font-medium text-slate-900">
                {formatGrams(cycle.refillAmountGr ?? 0)}
              </span>
            </div>
            <div className="mt-1 flex items-center justify-between text-slate-600">
              <span>Total servido</span>
              <span className="font-medium text-slate-900">
                {formatGrams(cycle.totalAmountFedGr ?? 0)}
              </span>
            </div>
          </div>
        )}

        {showDiscrepancyWarning && (
          <div className="flex items-start gap-2.5 rounded-lg bg-amber-50 px-3 py-2.5 ring-1 ring-amber-100">
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" aria-hidden="true" />
            <p className="text-xs text-amber-900">
              El sistema creía que {targetDispenser?.name ?? 'el dispenser'} tenía {formatGrams(dispenserCurrentStock)}.
              Va a quedar como 0 antes de cargar (manda lo que viste vos).
            </p>
          </div>
        )}

        {error && (
          <p className="text-sm text-status-red bg-red-50 rounded-lg px-3 py-2">{error}</p>
        )}

        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            fullWidth
            leftIcon={Package}
            isLoading={submitting}
            disabled={!isValid || submitting}
          >
            Cargar
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default RefillModal;
